// Story 10.6 — the logged-out `/c/{share_id}` certificate permalink page.
// Renders the SAME <PatronageCertificate> the owner sees in their vault
// (never a hand-mirrored fork), from the public DTO — no owner-private
// fields exist on it. Demo-grade: an SPA-only link does NOT unfurl (a
// crawler gets an empty shell); unfurl-quality OG rendering is the launch
// slice, out of scope here.

import { useParams } from "react-router-dom";
import {
  PatronageCertificate,
  isLoadableImageUrl,
  type PatronageCertificateProps,
} from "@greenhold/components";

import { certSerialFromShareId } from "../../lib/certSerial";
import { usePublicCertificate } from "../../features/public-collection/usePublicCollection";
import type { PublicShareDto } from "../../features/public-collection/publicCollectionTypes";
import { PublicShell, PublicNotFound, PublicLoading } from "./PublicShell";

export function publicCertProps(dto: PublicShareDto): PatronageCertificateProps {
  const portraitUrl = isLoadableImageUrl(dto.snapshot_image_signed_url)
    ? dto.snapshot_image_signed_url
    : null;
  return {
    tier: dto.tier,
    name: dto.snapshot_display_name,
    serial: certSerialFromShareId(dto.share_id),
    dossier: dto.portfolio_title,
    issued: dto.opened_at.slice(0, 10),
    condition: dto.condition,
    signed: dto.signed,
    portraitUrl,
    quote: dto.snapshot_hello_text || undefined,
  };
}

export function PublicCertificatePage() {
  const { shareId = "" } = useParams();
  const query = usePublicCertificate(shareId);

  if (query.isLoading) return <PublicLoading />;
  if (query.data?.notFound || !query.data?.data) {
    return (
      <PublicNotFound
        title="This certificate isn't public"
        detail="The link may be wrong, or its owner has taken it down."
      />
    );
  }

  const dto = query.data.data;
  return (
    <PublicShell>
      <div className="flex flex-col items-center gap-6">
        <PatronageCertificate width={320} {...publicCertProps(dto)} />
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold">
            {dto.snapshot_display_name}
          </h1>
          <p className="text-sm opacity-70">
            {dto.tier} · Certificate #{dto.share_serial} · from{" "}
            {dto.portfolio_title}
          </p>
        </div>
      </div>
    </PublicShell>
  );
}
